import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAppGlobStore } from 'src/appGlobalStore';
import { RoutePath } from 'src/data/Routes';
import { Log } from 'src/service/log.service';
import { DomUtil } from 'src/util/DomUtil';
import { GLOB } from 'src/util/Glob';
import {
  AUTOPLAY_MOUNT_TIMEOUT_MS, AUTOPLAY_TICK_MS, normalizeAutoplayInterval
} from './autoplayConst';
import {
  advanceAutoplay, dashboardPath, getMountedDashboardId, refreshAutoplayFolder,
  reportDashboardMounted, resyncAutoplay, setAutoplayHidden, setAutoplayInterval,
  setAutoplayNavigating, setTvActive, stopAutoplay, useAutoplayStore
} from './autoplayStore';

/**
 * Where the router sends us when the requested dashboard cannot be resolved. Landing on one of
 * these while still waiting for a mount is a dead dashboard, not the user navigating away.
 */
function isRouteFallback(pathname: string): boolean {
  return pathname === RoutePath.DASHBOARD || pathname === GLOB.getDefaultPath();
}

/**
 * Drives a playback session: navigation, the countdown, and the two ways a session ends by
 * itself. Lives in a component that stays mounted for the whole app, so the timers survive
 * every dashboard swap.
 */
export function useAutoplayController(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const active = useAutoplayStore(s => s.active);
  const navSeq = useAutoplayStore(s => s.navSeq);

  // the configured default seeds the bar's picker until someone chooses otherwise
  useEffect(() => {
    setAutoplayInterval(normalizeAutoplayInterval(GLOB.dashboardAutoplayInterval));
  }, []);

  // navigation picks the dashboard; the tree can land later and a reload can change the count
  useEffect(() => {
    refreshAutoplayFolder();
    let seen = useAppGlobStore.getState().leftMenu;
    return useAppGlobStore.subscribe(({ leftMenu }) => {
      if (leftMenu === seen) return;
      seen = leftMenu;
      refreshAutoplayFolder();
    });
  }, [location.pathname]);

  useEffect(() => {
    const onVisibility = () => setAutoplayHidden(document.hidden);
    // the single source of truth for TV mode - Esc and the browser's own exit come through here
    const onFullscreen = () => setTvActive(document.fullscreenElement?.id === DomUtil.TV_ROOT_ID);
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('fullscreenchange', onFullscreen);
    };
  }, []);

  useEffect(() => {
    if (!active) return;
    const { queue, index } = useAutoplayStore.getState();
    const target = queue[index];
    if (!target) return;

    const path = dashboardPath(target.id);
    useAutoplayStore.setState({ expectedPath: path });

    // already open: no navigation, nothing would report the mount
    if (getMountedDashboardId() === target.id && location.pathname === path) {
      reportDashboardMounted(target.id);
      return;
    }

    setAutoplayNavigating(true);
    navigate(path, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, navSeq]);

  useEffect(() => {
    if (!active) return;

    const timer = window.setInterval(() => {
      const state = useAutoplayStore.getState();
      // a pause or a hidden tab freezes both timers; thawing shifts them by the frozen span
      if (!state.active || state.paused || state.hidden) return;

      if (state.waitingSince != null) {
        if (Date.now() - state.waitingSince >= AUTOPLAY_MOUNT_TIMEOUT_MS) {
          Log.warn(`Dashboard "${state.queue[state.index]?.label}" did not open in time, skipping it.`);
          advanceAutoplay();
        }
        return;
      }

      if (state.deadlineAt == null) return;

      const remainingMs = state.deadlineAt - Date.now();
      if (remainingMs > 0) useAutoplayStore.setState({ remainingMs });
      else advanceAutoplay();
    }, AUTOPLAY_TICK_MS);

    return () => window.clearInterval(timer);
  }, [active]);

  // leaving the dashboards by hand ends playback; a filter change keeps the same path
  useEffect(() => {
    const state = useAutoplayStore.getState();
    if (!state.active || !state.expectedPath || location.pathname === state.expectedPath) return;

    if (state.waitingSince != null && isRouteFallback(location.pathname)) {
      Log.warn(`Dashboard "${state.queue[state.index]?.label}" is not available, skipping it.`);
      advanceAutoplay();
      return;
    }

    const picked = state.queue.findIndex(item => dashboardPath(item.id) === location.pathname);
    if (picked >= 0) {
      resyncAutoplay(picked);
      return;
    }

    stopAutoplay();
  }, [location.pathname]);
}
